// Service layer for loan creation and MISMO parsing
// This will later integrate with LOS APIs (e.g., Vesta)

/**
 * Creates a new loan in Draft state
 * @param {Object} loanData - Loan data following MISMO 3.4 structure
 * @returns {Promise<Object>} Created loan object
 */
export const createNewLoan = async (loanData) => {
  // Mock implementation - will later call LOS API
  return new Promise((resolve) => {
    setTimeout(() => {
      const newLoan = {
        id: Date.now(),
        loanNumber: `UFF-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
        status: 'Draft',
        createdDate: new Date().toISOString(),
        createdBy: 'Current User',
        ...loanData,
        // MISMO 3.4 structure
        mismo: {
          version: '3.4',
          dealSetIdentifier: `DEAL-${Date.now()}`,
          ...loanData.mismo
        }
      };
      resolve(newLoan);
    }, 500);
  });
};

/**
 * Validates MISMO 3.4 XML file
 * @param {File} file - XML file to validate
 * @returns {Promise<Object>} Validation result
 */
export const validateMISMOFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const xmlText = e.target.result;
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
        
        // Check for parsing errors
        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
          reject({
            valid: false,
            error: 'Invalid XML format',
            details: 'The file could not be parsed as valid XML'
          });
          return;
        }
        
        // Check for MISMO root element
        const mismoRoot = xmlDoc.querySelector('MESSAGE, MISMO');
        if (!mismoRoot) {
          reject({
            valid: false,
            error: 'Not a MISMO file',
            details: 'The XML file does not contain a MISMO root element'
          });
          return;
        }
        
        resolve({
          valid: true,
          xmlDoc: xmlDoc,
          xmlText: xmlText
        });
      } catch (error) {
        reject({
          valid: false,
          error: 'File read error',
          details: error.message
        });
      }
    };
    
    reader.onerror = () => {
      reject({
        valid: false,
        error: 'File read error',
        details: 'Could not read the file'
      });
    };
    
    reader.readAsText(file);
  });
};

/**
 * Parses MISMO 3.4 XML into internal loan model
 * @param {Document} xmlDoc - Parsed XML document
 * @param {string} loanType - Required loan type (Mortgage, HELOC, HomeEquityLoan)
 * @returns {Object} Parsed loan data
 */
export const parseMISMOXML = (xmlDoc, loanType) => {
  // Helper function to safely get text content
  const getTextContent = (selector, defaultValue = '') => {
    const element = xmlDoc.querySelector(selector);
    return element ? element.textContent.trim() : defaultValue;
  };
  
  // Helper function to get attribute value
  const getAttribute = (selector, attribute, defaultValue = '') => {
    const element = xmlDoc.querySelector(selector);
    return element ? (element.getAttribute(attribute) || defaultValue) : defaultValue;
  };
  
  // Parse borrower information
  const borrowerFirstName = getTextContent('INDIVIDUAL_NAME FirstName, PARTY INDIVIDUAL FirstName');
  const borrowerLastName = getTextContent('INDIVIDUAL_NAME LastName, PARTY INDIVIDUAL LastName');
  const borrowerMiddleName = getTextContent('INDIVIDUAL_NAME MiddleName, PARTY INDIVIDUAL MiddleName');
  
  // Parse property information
  const propertyAddress = getTextContent('PROPERTY ADDRESS AddressLineText, SUBJECT_PROPERTY ADDRESS AddressLineText');
  const propertyCity = getTextContent('PROPERTY ADDRESS CityName, SUBJECT_PROPERTY ADDRESS CityName');
  const propertyState = getTextContent('PROPERTY ADDRESS StateCode, SUBJECT_PROPERTY ADDRESS StateCode');
  const propertyZip = getTextContent('PROPERTY ADDRESS PostalCode, SUBJECT_PROPERTY ADDRESS PostalCode');
  
  // Parse loan information
  const loanAmount = parseFloat(getTextContent('LOAN_AMOUNT, LOAN LoanAmount, TERMS_OF_LOAN LoanAmount')) || 0;
  const loanPurpose = getTextContent('LOAN_PURPOSE_TYPE, LOAN LoanPurposeType');
  const interestRate = parseFloat(getTextContent('INTEREST_RATE, LOAN NoteRatePercent')) || 0;
  
  // Parse financial information
  const purchasePrice = parseFloat(getTextContent('SALES_PRICE, PROPERTY SalesContractAmount')) || 0;
  const downPayment = parseFloat(getTextContent('DOWN_PAYMENT, LOAN DownPaymentAmount')) || 0;
  const creditScore = parseInt(getTextContent('CREDIT_SCORE, BORROWER CreditScoreValue')) || 0;
  
  // Build loan object following MISMO structure
  const loanData = {
    loanType: loanType, // Required user input
    loanPurpose: loanPurpose || 'Purchase',
    loanAmount: loanAmount,
    interestRate: interestRate,
    
    // Borrower information
    borrower: {
      firstName: borrowerFirstName,
      middleName: borrowerMiddleName,
      lastName: borrowerLastName,
      fullName: `${borrowerFirstName} ${borrowerMiddleName ? borrowerMiddleName + ' ' : ''}${borrowerLastName}`.trim(),
      creditScore: creditScore
    },
    
    // Property information
    property: {
      address: propertyAddress,
      city: propertyCity,
      state: propertyState,
      zipCode: propertyZip,
      purchasePrice: purchasePrice
    },
    
    // Financial details
    financial: {
      loanAmount: loanAmount,
      downPayment: downPayment,
      purchasePrice: purchasePrice,
      ltv: purchasePrice > 0 ? ((loanAmount / purchasePrice) * 100).toFixed(2) : 0
    },
    
    // MISMO metadata
    mismo: {
      version: '3.4',
      imported: true,
      importDate: new Date().toISOString()
    }
  };
  
  return loanData;
};

/**
 * Imports MISMO 3.4 XML file and creates loan
 * @param {File} file - XML file
 * @param {string} loanType - Required loan type
 * @returns {Promise<Object>} Created loan object
 */
export const importMISMOLoan = async (file, loanType) => {
  try {
    // Validate file
    const validation = await validateMISMOFile(file);
    
    if (!validation.valid) {
      throw new Error(validation.error);
    }
    
    // Parse XML
    const loanData = parseMISMOXML(validation.xmlDoc, loanType);
    
    // Store original XML for traceability
    loanData.originalXML = validation.xmlText;
    
    // Validate required fields after import
    const missingFields = [];
    if (!loanData.borrower.firstName || !loanData.borrower.lastName) {
      missingFields.push('Borrower Name');
    }
    if (!loanData.property.state) {
      missingFields.push('Property State');
    }
    if (!loanData.loanAmount || loanData.loanAmount === 0) {
      missingFields.push('Loan Amount');
    }
    
    if (missingFields.length > 0) {
      loanData.validationWarnings = missingFields;
    }
    
    // Create loan
    const newLoan = await createNewLoan(loanData);
    
    return {
      success: true,
      loan: newLoan,
      warnings: missingFields.length > 0 ? `Missing required fields: ${missingFields.join(', ')}` : null
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to import MISMO file'
    };
  }
};

/**
 * Validates loan data before creation
 * @param {Object} loanData - Loan data to validate
 * @returns {Object} Validation result
 */
export const validateLoanData = (loanData) => {
  const errors = {};
  
  // Required fields
  if (!loanData.loanPurpose) {
    errors.loanPurpose = 'Loan purpose is required';
  }
  
  if (!loanData.loanType) {
    errors.loanType = 'Loan type is required';
  }
  
  if (!loanData.borrower?.firstName) {
    errors.borrowerFirstName = 'Borrower first name is required';
  }
  
  if (!loanData.borrower?.lastName) {
    errors.borrowerLastName = 'Borrower last name is required';
  }
  
  if (!loanData.property?.state) {
    errors.propertyState = 'Property state is required';
  }
  
  if (!loanData.loanAmount || loanData.loanAmount <= 0) {
    errors.loanAmount = 'Estimated loan amount is required and must be greater than 0';
  }
  
  return {
    valid: Object.keys(errors).length === 0,
    errors: errors
  };
};

// Export loan types as constants
export const LOAN_TYPES = {
  MORTGAGE: 'Mortgage',
  HELOC: 'HELOC',
  HOME_EQUITY_LOAN: 'HomeEquityLoan'
};

export const LOAN_PURPOSES = {
  PURCHASE: 'Purchase',
  REFINANCE: 'Refinance'
};
